import snakeCase from 'lodash/snakeCase'
import { useRouter } from 'next/router'
import { useCallback, useContext, useState } from 'react'

import { handleEvent } from '@Events/Manager'
import { OtherEvent } from '@Events/Other'
import { useSubscribeToNewsletterMutation } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { identify, setUserAttributes } from '@Globals/MParticle/User'
import Sentry from '@Globals/Sentry'
import { enableService } from '@Globals/Usercentrics/enableService'

type SubscribeDataFormat = {
  campaignLabel?: string
  email: string
  name?: string
  source?: string
}

type UseNewsletterSubscriptionReturn = {
  loading: boolean
  subscribe: (data: SubscribeDataFormat) => Promise<boolean>
}

export const useNewsletterSubscription = (): UseNewsletterSubscriptionReturn => {
  const [performSubscribeQuery] = useSubscribeToNewsletterMutation()
  const [loading, setLoading] = useState(false)
  const { locale = '' } = useRouter()
  const { store } = useContext(MetadataContext)
  const storeEmail = store?.config?.email || ''

  const subscribe = useCallback(async ({ campaignLabel = '', email, source = '' }: SubscribeDataFormat) => {
    setLoading(true)

    enableService('mParticle')

    // mParticle might be blocked by the user, using a fancy adblocker, and since we're stubbing
    // the mParticle functions, we would never know. However, we do not stub ALL the functions,
    // so by looking at an "unstubbed" function, and checking to see if it exists, we'll know whether
    // or not the script will work. If mParticle isn't available, the identity lookups would hang
    // indefinitely, and the signup will never work. This fixes that.
    //
    // @ts-ignore // Because "mParticle" doesn't exist on `window`
    if (typeof window.mParticle.getAppVersion === 'function') {
      // Convert the source ID and campaign label to something nice for tracking porpoises. I mean, purposes.
      const sourceName = campaignLabel
        ? `trendhim_ecom_${snakeCase(source)}_${snakeCase(campaignLabel)}`
        : `trendhim_ecom_${snakeCase(source)}`

      try {
        // Identify the user with mParticle.
        await identify({ email })

        // Set some neat user attributes for later.
        await setUserAttributes({
          forward_trendhim_ecom_customer_io: 'true',
          trendhim_ecom_newsletter_local_email: storeEmail,
          trendhim_ecom_newsletter_locale: locale,
          trendhim_ecom_newsletter_source: sourceName,
        })
      } catch (e) {
        Sentry.captureException(e)
      }
    }

    // Tell mParticle that we wanna sign up, yo!
    handleEvent(
      new OtherEvent({
        attributes: {
          campaign_label: campaignLabel,
          source_id: source,
        },
        eventName: 'newsletter_opt_in',
      }),
    )

    try {
      // Actually perform the subscription through Graph.
      await performSubscribeQuery({ variables: { campaign: campaignLabel, email, source } })
      setLoading(false)

      return true
    } catch {
      setLoading(false)
      return false
    }
  }, [locale, performSubscribeQuery, storeEmail])

  return {
    loading,
    subscribe,
  }
}
